/** Personal recovery secrets are generated locally and never sent to the API. */
import { z } from "zod";
import { base64ToBytes, bytesToBase64 } from "./crypto.js";
import {
  generateSalt, rewrapPrivateKey,
  deriveKeyFromPassword, unwrapPrivateKey, unwrapFromPrivateKey,
} from "./keyring.js";

export const recoveryBackupSchema = z.object({
  format: z.literal("myfriends-recovery-v1"),
  userId: z.string().min(1).max(80),
  username: z.string().min(1).max(32),
  publicKey: z.string().min(1).max(8192),
  code: z.string().regex(/^FC1-[A-Za-z0-9_-]{43}$/),
  wrappedPrivateKey: z.string().min(1).max(8192),
  salt: z.string().min(1).max(256),
});
export type RecoveryBackup = z.infer<typeof recoveryBackupSchema>;

async function codeKey(code: string, salt: string): Promise<CryptoKey> {
  if (!/^FC1-[A-Za-z0-9_-]{43}$/.test(code)) throw new Error("That recovery code is not valid.");
  const raw = base64ToBytes(code.slice(4).replace(/-/g, "+").replace(/_/g, "/") + "=");
  if (raw.length !== 32) throw new Error("That recovery code is not valid.");
  const material = await crypto.subtle.importKey("raw", new Uint8Array(raw), "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({
    name: "HKDF", hash: "SHA-256", salt: new Uint8Array(base64ToBytes(salt)),
    info: new TextEncoder().encode("myfriends/account-recovery/v1"),
  }, material, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

export async function createRecoveryBackup(
  identity: { userId: string; username: string },
  bundle: { publicKey: string; wrappedPrivateKey: string; keySalt: string; kdfIterations: number },
  password: string,
): Promise<RecoveryBackup> {
  const raw = crypto.getRandomValues(new Uint8Array(32));
  const code = "FC1-" + bytesToBase64(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  const salt = bytesToBase64(generateSalt());
  const oldKey = await deriveKeyFromPassword(password, base64ToBytes(bundle.keySalt), bundle.kdfIterations);
  const wrappedPrivateKey = await rewrapPrivateKey(bundle.wrappedPrivateKey, oldKey, await codeKey(code, salt));
  raw.fill(0);
  return { format: "myfriends-recovery-v1", ...identity, publicKey: bundle.publicKey, code, wrappedPrivateKey, salt };
}

export async function openRecoveryBackup(backup: RecoveryBackup): Promise<CryptoKey> {
  return unwrapPrivateKey(backup.wrappedPrivateKey, await codeKey(backup.code, backup.salt));
}

export async function resealRecoveryBackup(backup: RecoveryBackup, password: string) {
  const salt = generateSalt();
  const kdfIterations = 600_000;
  const newKey = await deriveKeyFromPassword(password, salt, kdfIterations);
  return {
    publicKey: backup.publicKey,
    wrappedPrivateKey: await rewrapPrivateKey(backup.wrappedPrivateKey, await codeKey(backup.code, backup.salt), newKey),
    keySalt: bytesToBase64(salt), kdfIterations,
  };
}

export async function recoveryProof(challenge: string, privateKey: CryptoKey): Promise<string> {
  return bytesToBase64(await unwrapFromPrivateKey(challenge, privateKey));
}

export async function publicKeyFingerprint(publicKey: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new Uint8Array(base64ToBytes(publicKey))));
  return [...digest].map((byte) => byte.toString(16).padStart(2, "0")).join("").match(/.{1,8}/g)!.join(" ");
}
